import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Plus,
  Pencil,
  Trash2,
  Globe,
  Upload,
  Download,
  Zap,
} from "lucide-react";
import {
  fetchMultilingualStrings,
  deleteMultilingualString,
  clearError,
} from "../../redux/slices/multilingualStringSlice";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useToast } from "../../components/ui/use-toast";
import MultilingualStringDialog from "./MultilingualStringDialog";
import SimpleMultilingualStringDialog from "./SimpleMultilingualStringDialog";

const MultilingualStrings = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { data: multilingualStrings, loading } = useSelector(
    (state) => state.multilingualString
  );

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingString, setEditingString] = useState(null);
  const [isAdvancedDialogOpen, setIsAdvancedDialogOpen] = useState(false);
  const [advancedDialogDefaultTab, setAdvancedDialogDefaultTab] =
    useState("single");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stringToDelete, setStringToDelete] = useState(null);
  const [dialogDefaultTab, setDialogDefaultTab] = useState("single");

  const stringsFetched = React.useRef(false);

  useEffect(() => {
    if (!stringsFetched.current) {
      stringsFetched.current = true;
      dispatch(fetchMultilingualStrings());
    }
  }, [dispatch]);

  const handleCreateClick = (tab = "single", advanced = false) => {
    if (advanced) {
      setAdvancedDialogDefaultTab(tab);
      setIsAdvancedDialogOpen(true);
    } else {
      setIsCreateDialogOpen(true);
    }
  };

  const handleEdit = (multilingualString) => {
    setEditingString(multilingualString);
  };

  const handleDeleteClick = (multilingualString) => {
    setStringToDelete(multilingualString);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteMultilingualString(stringToDelete.key)).unwrap();
      toast({
        title: "Success",
        description: "Multilingual string deleted successfully",
      });
      setDeleteDialogOpen(false);
      setStringToDelete(null);
      dispatch(fetchMultilingualStrings()); // Refresh the list
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to delete multilingual string",
        variant: "destructive",
      });
    }
  };

  const getLanguageName = (code) => {
    const languageNames = {
      en: "English",
      ar: "العربية",
      ur: "اردو",
      fr: "Français",
      es: "Español",
    };
    return languageNames[code] || code.toUpperCase();
  };

  const getCategoryColor = (category) => {
    const colors = {
      common: "bg-blue-100 text-blue-800",
      auth: "bg-green-100 text-green-800",
      navigation: "bg-purple-100 text-purple-800",
      quiz: "bg-orange-100 text-orange-800",
      profile: "bg-pink-100 text-pink-800",
      settings: "bg-gray-100 text-gray-800",
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  // Flatten the grouped data for display
  const flattenedStrings = [];
  if (multilingualStrings) {
    Object.entries(multilingualStrings).forEach(([category, strings]) => {
      strings.forEach((string) => {
        flattenedStrings.push({
          ...string,
          category,
        });
      });
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Multilingual Strings</h1>
          <p className="text-muted-foreground">
            Manage text strings and their translations for the mobile app
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Zap className="mr-2 h-4 w-4" />
                Quick Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleCreateClick("single", true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Single String (Advanced)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleCreateClick("bulk", true)}>
                <Upload className="mr-2 h-4 w-4" />
                Bulk Import
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  // Export functionality
                  const dataStr = JSON.stringify(multilingualStrings, null, 2);
                  const dataUri =
                    "data:application/json;charset=utf-8," +
                    encodeURIComponent(dataStr);
                  const exportFileDefaultName = "multilingual-strings.json";
                  const linkElement = document.createElement("a");
                  linkElement.setAttribute("href", dataUri);
                  linkElement.setAttribute("download", exportFileDefaultName);
                  linkElement.click();
                  toast({
                    title: "Success",
                    description: "Strings exported successfully",
                  });
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                Export JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => handleCreateClick()}>
            <Plus className="mr-2 h-4 w-4" />
            Add String
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="mr-2 h-5 w-5" />
            String Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">Loading strings...</p>
              </div>
            </div>
          ) : flattenedStrings.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold">No strings found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Get started by adding your first multilingual string.
              </p>
              <div className="mt-6">
                <Button onClick={() => handleCreateClick()}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add String
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flattenedStrings.map((string) => (
                  <TableRow key={string.key}>
                    <TableCell className="font-medium">
                      <code className="bg-muted px-2 py-1 rounded text-sm">
                        {string.key}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCategoryColor(string.category)}>
                        {string.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(string.translations).map((lang) => (
                          <Badge
                            key={lang}
                            variant="outline"
                            className="text-xs"
                          >
                            {getLanguageName(lang)}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-sm text-muted-foreground truncate">
                        {string.description || "No description"}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(string)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(string)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog - Simple */}
      <SimpleMultilingualStringDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        multilingualString={null}
      />

      {/* Edit Dialog - Simple */}
      <SimpleMultilingualStringDialog
        open={!!editingString}
        onClose={() => setEditingString(null)}
        multilingualString={editingString}
      />

      {/* Advanced Dialog */}
      <MultilingualStringDialog
        open={isAdvancedDialogOpen}
        onClose={() => setIsAdvancedDialogOpen(false)}
        multilingualString={null}
        defaultTab={advancedDialogDefaultTab}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Multilingual String</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the string "{stringToDelete?.key}
              "? This action cannot be undone and may affect the mobile app if
              this string is currently in use.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultilingualStrings;
